/**
 * Every submission the public form has ever received (Plan D12).
 *
 * An **archive**, not a work list. It used to default to `SUBMITTED` and carry
 * the decision, which made it a second queue competing with the volunteers
 * screen; now the work happens there, in the panel at the top, and this is where
 * you come to look something up — what did we reject last quarter, and why; what
 * exactly did this person send us in March.
 *
 * That is why the status filter starts on **all**. Defaulting it to `SUBMITTED`
 * would still say "this is your to-do list", which is precisely the message D12
 * removed.
 *
 * Not in the sidebar. Reached from the submissions panel, and from any volunteer
 * the submission created.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { REGISTRATION_STATUSES } from '@parinaam-vms/shared';
import type { RegistrationRequestListItem } from '@parinaam-vms/shared';
import {
  Card,
  DataTable,
  DuplicateFlag,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  Pager,
  SelectInput,
  StatusBadge,
  TextInput,
} from '../../components/common';
import { CRUMB } from '../../constants/breadcrumbs';
import { ROWS_PER_PAGE } from '../../constants/pagination';
import { useRegistrations } from '../../hooks/useRegistrations';
import { countedTitle, formatDate } from '../../utils/format';

const STATUS_OPTIONS = REGISTRATION_STATUSES.map((status) => ({
  value: status,
  label: status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase()),
}));

export function RegistrationsPage() {
  const navigate = useNavigate();
  // Plan D12: all statuses. This is an archive, and a default of SUBMITTED
  // would still read as a work queue.
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const query = useRegistrations({
    status: status || undefined,
    search: search.trim() || undefined,
    page,
    page_size: ROWS_PER_PAGE,
  });

  const rows = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="All submissions"
        description="Every registration, exactly as it was sent."
        breadcrumbs={[CRUMB.registrations]}
      />

      <Card title={countedTitle('Submissions', query.data?.pagination?.total)}>
        <FilterBar label="Filter registrations">
          <TextInput
            label="Search"
            placeholder="Name, email, phone or code"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              // A filter change with the old page number lands on an empty page
              // whenever the new result set is shorter.
              setPage(1);
            }}
          />
          <SelectInput
            label="Status"
            placeholder="All statuses"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          />
        </FilterBar>

        {query.isPending ? (
          <LoadingState label="Loading registrations…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No submissions found"
            message={
              search || status
                ? 'No registration matches these filters.'
                : 'Registrations submitted through the public form will appear here.'
            }
          />
        ) : (
          <>
            <Pager pagination={query.data?.pagination} onPageChange={setPage} />
            <DataTable<RegistrationRequestListItem>
              caption="Registration requests"
              rows={rows}
              getRowKey={(row) => row.registration_request_id}
              onRowClick={(row) => navigate(`/registrations/${row.registration_request_id}`)}
              columns={[
                { key: 'code', header: 'Reference', render: (row) => row.registration_code },
                {
                  key: 'name',
                  header: 'Name',
                  render: (row) => (
                    <>
                      {[row.first_name, row.last_name].filter(Boolean).join(' ')}
                      {/*
                        Beside the name, not in a column of its own: the column
                        would be empty on almost every row, and this is worth
                        knowing at the moment somebody reads who the submission
                        is from. Before 25 Sep 2026 the list could not show it at
                        all, and a repeat submission looked like a first one.
                      */}
                      {row.possible_duplicate_of && (
                        <>
                          {' '}
                          <DuplicateFlag />
                        </>
                      )}
                    </>
                  ),
                },
                {
                  key: 'contact',
                  header: 'Contact',
                  render: (row) => row.email ?? row.phone ?? '—',
                },
                {
                  key: 'submitted',
                  header: 'Submitted',
                  render: (row) => formatDate(row.submitted_at),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (row) => <StatusBadge status={row.status} />,
                },
              ]}
            />
          </>
        )}
      </Card>
    </>
  );
}
